import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Question, QUESTION_TYPE } from 'types/question.types';
import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import { cn } from '@epoch-ui/lib/utils';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import {
  getAggregatedResponses,
  AggregatedResponsesResult
} from '@event-editor/api/feedback/getAggregatedResponses';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';

import { HorizontalBarChart } from '../QuestionResponsesCharts/HorizontalBarChart';
import { VerticalBarChart } from '../QuestionResponsesCharts/VerticalBarChart';

interface AggregatedQuestionResponsesProps {
  question: Question;
  responsesTotal: number;
}

interface ResponseOption {
  option: string;
  count: number;
}

export const AggregatedQuestionResponses: React.FC<AggregatedQuestionResponsesProps> = ({
  question,
  responsesTotal
}) => {
  const { showToast } = useToaster();

  const {
    data: queryData,
    isLoading,
    error
  } = useQuery<AggregatedResponsesResult, Error>({
    queryKey: eventEditorQueryKeys.eventAggregatedQuestionnaireResponses(Number(question.id)),
    queryFn: () =>
      getAggregatedResponses({
        questionId: Number(question.id)
      }),
    enabled: typeof question.id === 'number' && !Number.isNaN(question.id)
  });

  const totalResponses = queryData?.aggregatedResponses.total || 0;

  if (error) {
    console.error('Failed to fetch aggregated responses:', error);
    showToast('Failed to load question responses. Please try again.', {
      variant: ToasterVariant.ERROR
    });
  }

  const chartData = useMemo(() => {
    const responseOptions = (queryData?.aggregatedResponses.options || []) as ResponseOption[];
    return responseOptions.map(({ option, count }) => ({
      name: option,
      value: count,
      percentage: Math.round((count / (totalResponses || 1)) * 100)
    }));
  }, [queryData?.aggregatedResponses.options, queryData?.aggregatedResponses.total]);

  if (isLoading) {
    return (
      <div className="tw-space-y-4">
        <Skeleton className="tw-h-4 tw-w-48" />
        <Skeleton className="tw-h-[350px] tw-w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="tw-mb-3">
        <p className="tw-mb-1.5 tw-text-sm tw-font-medium tw-leading-4 tw-text-black">
          {question.label}
        </p>
        <p className={cn('tw-m-0 tw-text-xs tw-leading-4 tw-text-primary-grey')}>
          {totalResponses} of {responsesTotal} responses
        </p>
      </div>
      <div className="tw-rounded-lg tw-border tw-border-solid tw-border-border-grey tw-bg-background tw-p-6">
        {totalResponses === 0 ? (
          <p className="tw-mb-0 tw-text-sm tw-leading-5 tw-text-medium-grey">
            No responses for this question.
          </p>
        ) : (
          <>
            {question.type === QUESTION_TYPE.RADIO || question.type === QUESTION_TYPE.CHECK ? (
              <div className="tw-h-[350px]">
                <HorizontalBarChart data={chartData} />
              </div>
            ) : (
              <VerticalBarChart
                data={chartData}
                properties={question.properties || {}}
                type={question.type}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};
